/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IS_DEV } from 'helpers/config'
import type { Payment } from 'types/entities/Payment'
import type { Article } from 'types/entities/Article'
import type { CloudSyncQueue } from 'types/entities/CloudSyncQueue'
import type { Reservation } from 'types/entities/Reservation'

type CommonScalar = string | number | boolean | null
type CommonFlatObject = Record<string, CommonScalar | CommonScalar[]>
type DebugMessages = Record<string, string[]>
type DebugApiErrorPayload = {
    errors?: DebugMessages
    message?: string
    code?: number
    data?: {
        messages?: DebugMessages
    }
}
type DebugPayload = string | CommonFlatObject | DebugApiErrorPayload
type CommonResultPayload = CommonScalar | CommonScalar[] | CommonFlatObject

export interface CommonState {
    ajaxLoading: number
    ajaxError: string
    debug: DebugPayload | null
    payment: Payment | CommonFlatObject | null
    result: CommonResultPayload | null
    articles: Article[] | CommonFlatObject | null
    queues: CloudSyncQueue[] | CommonFlatObject | null
    reservations: Reservation[] | CommonFlatObject | null
}

const initialState: CommonState = {
    ajaxLoading: 0,
    ajaxError: '',
    debug: null,
    payment: null,
    result: null,
    articles: null,
    queues: null,
    reservations: null,
}

const commonSlice = createSlice({
    name: 'common',
    initialState,
    reducers: {
        setAjaxLoading(state, action: PayloadAction<{ loading: number; url?: string }>) {
            state.ajaxLoading = Math.max(0, state.ajaxLoading + action.payload.loading)
        },
        setAjaxError(state, action: PayloadAction<string>) {
            state.ajaxError = action.payload
        },
        setDebug(state, action: PayloadAction<DebugPayload>) {
            if (IS_DEV) {
                state.debug = action.payload
            }
        },
        setPayment(state, action: PayloadAction<Payment | CommonFlatObject>) {
            state.payment = action.payload
        },
        setResult(state, action: PayloadAction<CommonResultPayload>) {
            state.result = action.payload
        },
        setArticles(state, action: PayloadAction<Article[] | CommonFlatObject>) {
            state.articles = action.payload
        },
        setQueues(state, action: PayloadAction<CloudSyncQueue[] | CommonFlatObject>) {
            state.queues = action.payload
        },
        setReservations(state, action: PayloadAction<Reservation[] | CommonFlatObject>) {
            state.reservations = action.payload
        },
        resetState: () => initialState,
    },
})

export const {
    setAjaxLoading,
    setAjaxError,
    setDebug,
    setPayment,
    setResult,
    setArticles,
    setQueues,
    setReservations,
    resetState,
} = commonSlice.actions

export default commonSlice.reducer
